import { array, object, oneOf, optional, str, unknownValue, type FieldParser } from 'validation';

// The list-table config envelope every paginated list endpoint returns
// alongside its rows (recurrence_definitions, locks, audit_trail, folders, …),
// plus the canonical types + schemas for it. Shared here — not re-declared per
// resource — because it is list-table infrastructure produced by the same
// backend helpers (headerConfig + buildFiltersConfig) for all of them and
// consumed by one shared surface (`useListQuery` / `useTableController`).

// The runtime list of filter types, for `oneOf` validation. Canonical source:
// `FilterType` derives from it, so the two can't drift and the list is asserted
// complete (adding a type is a one-line change here).
export const FILTER_TYPES = ['search', 'date', 'dropdown', 'multi-dropdown', 'checkbox'] as const;
export type FilterType = (typeof FILTER_TYPES)[number];

export interface FilterConfig {
  key: string;
  label: string;
  type: FilterType;
  options?: string[];
  // Opaque seed value for the filter control; read only via a nullish default.
  default?: unknown;
}

/**
 * A column-header config entry: what the table renders per column, plus the
 * key the backend maps to a filter type.
 */
export interface ListColumnConfig {
  readonly header_key: string;
  readonly label: string;
  readonly header_type: string;
}

// A drifted `type` degrades to 'search' rather than failing the whole list.
export const filterConfigSchema: FieldParser<FilterConfig> = object<FilterConfig>({
  key: str(),
  label: str(),
  type: oneOf(FILTER_TYPES, 'search'),
  options: optional(array(str())),
  default: optional(unknownValue()),
});

export const listColumnConfigSchema: FieldParser<ListColumnConfig> = object<ListColumnConfig>({
  header_key: str(),
  label: str(),
  header_type: str(),
});
